package com.islamicmedia.shared.presentation

import com.islamicmedia.shared.data.api.AnthropicApiClient
import com.islamicmedia.shared.data.auth.SupabaseAuthService
import com.islamicmedia.shared.data.ocr.TextRecognizer
import com.islamicmedia.shared.data.storage.KidsModeStore
import com.islamicmedia.shared.domain.model.AnalysisProgress
import com.islamicmedia.shared.domain.model.DisambiguationItem
import com.islamicmedia.shared.domain.model.Entitlement
import com.islamicmedia.shared.domain.model.KidsAgeBand
import com.islamicmedia.shared.domain.model.KidsAlternative
import com.islamicmedia.shared.domain.model.KidsBlock
import com.islamicmedia.shared.domain.model.KidsCacheRead
import com.islamicmedia.shared.domain.model.KidsProgress
import com.islamicmedia.shared.domain.model.KidsScan
import com.islamicmedia.shared.domain.model.KidsScanStatus
import com.islamicmedia.shared.domain.model.MediaCandidate
import com.islamicmedia.shared.domain.model.MediaType
import com.islamicmedia.shared.domain.model.SearchCriteria
import com.islamicmedia.shared.domain.repository.MediaCheckerRepository
import com.islamicmedia.shared.util.Sha256
import com.islamicmedia.shared.util.currentTimeMillis
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Deferred
import kotlinx.coroutines.async
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json

/** Something to tell the kid in a friendly bubble. The UI owns the copy. */
enum class KidsMessage { NOT_FOUND, PHOTO_UNREADABLE, OOPS }

enum class PinResetStep { IDLE, SENDING, CODE_SENT, VERIFYING, VERIFIED, FAILED }

sealed class PinExitResult {
    object Ok : PinExitResult()
    data class Wrong(val attemptsLeft: Int) : PinExitResult()
    data class Locked(val untilMs: Long) : PinExitResult()
}

data class KidsUiState(
    /** Kids Mode is on: the app must show ONLY the kids screens. */
    val enabled: Boolean = false,
    val hasPin: Boolean = false,
    val query: String = "",
    val isSearching: Boolean = false,
    val isReadingPhoto: Boolean = false,
    /** "Pick one" list — disambiguation results, max [KidsModeController.MAX_CHOICES]. */
    val choices: List<DisambiguationItem> = emptyList(),
    /** Known titles matching what's typed so far (like the main search bar's), max [KidsModeController.MAX_SUGGESTIONS]. */
    val suggestions: List<DisambiguationItem> = emptyList(),
    /** Several titles found in one photo — pick which one. */
    val photoChoices: List<MediaCandidate> = emptyList(),
    /** Newest first. */
    val scans: List<KidsScan> = emptyList(),
    /** Rough 0..1 progress per CHECKING scan (not persisted). */
    val progress: Map<String, Float> = emptyMap(),
    /** The scan whose waiting / result screen is open. */
    val openScanId: String? = null,
    val message: KidsMessage? = null,
    /** Non-null = show the "Ask a grown-up" card. */
    val block: KidsBlock? = null,
    val pinLockedUntil: Long = 0L,
    val resetStep: PinResetStep = PinResetStep.IDLE,
    /** Who KIDZONE is set up for, chosen by the parent. Null = not chosen yet (setup asks before Start). */
    val ageBand: KidsAgeBand? = null,
    /** 8 and over: "Try one of these instead" per scan id, once looked up (empty = none found). */
    val alternatives: Map<String, List<KidsAlternative>> = emptyMap()
) {
    val openScan: KidsScan? get() = scans.firstOrNull { it.id == openScanId }
    /** The band the screens use. A device set up before the bands existed is treated as 7 and under (the simpler, stricter one). */
    val band: KidsAgeBand get() = ageBand ?: KidsAgeBand.YOUNG
}

/**
 * Everything Kids Mode does, kept OUT of [MediaCheckerViewModel] on purpose: this class only ever
 * talks to the kids endpoints (kids=true) and never holds an adult AnalysisResult, so there is no
 * code path by which adult text could reach a child's screen. Owned by the ViewModel (which shares
 * its repository, API client and scope) and exposed to both UIs as `viewModel.kids`.
 *
 * The PIN never leaves the device: it's stored as a salted SHA-256 in [KidsModeStore], which is
 * separate from SettingsStorage so a spurious sign-out can't clear the lock.
 */
class KidsModeController(
    private val scope: CoroutineScope,
    private val repository: MediaCheckerRepository,
    private val apiClient: AnthropicApiClient,
    private val textRecognizer: TextRecognizer,
    private val entitlement: () -> Entitlement?,
    private val criteria: () -> SearchCriteria,
    /** Re-read the entitlement, so a switched-off (or newly switched-on) account is noticed quickly. */
    private val refreshEntitlement: () -> Unit = {},
    /** The titles the main search bar suggests from (the cached titles index). */
    private val titles: () -> List<DisambiguationItem> = { emptyList() },
    private val store: KidsModeStore = KidsModeStore()
) {
    private val json = Json { ignoreUnknownKeys = true }
    private val _state = MutableStateFlow(KidsUiState())
    val state: StateFlow<KidsUiState> = _state.asStateFlow()

    private val jobs = mutableMapOf<String, Job>()
    private var searchJob: Job? = null
    private var photoJob: Job? = null
    private var resetToken: String? = null
    /** Cover art looked up while the kid reads the choices, so the picked title shows its picture at once. */
    private val covers = mutableMapOf<String, String>()
    /** From the last search, for the "pick one" tap that follows it. */
    private var pendingJourneyId: String? = null
    private var pendingSource: String = "search"

    init {
        val enabled = store.getString(KEY_ENABLED) == "1"
        _state.update {
            it.copy(
                enabled = enabled,
                hasPin = store.getString(KEY_PIN) != null,
                scans = if (enabled) loadScans() else emptyList(),
                pinLockedUntil = store.getString(KEY_LOCKED_UNTIL)?.toLongOrNull() ?: 0L,
                ageBand = KidsAgeBand.parse(store.getString(KEY_AGE_BAND))
            )
        }
        if (enabled) reattachChecking()
    }

    // ── Parent side: PIN + entering ─────────────────────────────────────────────

    /** Set (or replace) the PIN. 4 digits. Returns false if it isn't. */
    fun setPin(pin: String): Boolean {
        if (!isValidPin(pin)) return false
        val salt = Sha256.hex("${currentTimeMillis()}-${kotlin.random.Random.nextLong()}").take(16)
        store.putString(KEY_PIN, "$salt:${Sha256.hex("$salt:$pin")}")
        store.putString(KEY_FAILS, null)
        store.putString(KEY_LOCKED_UNTIL, null)
        _state.update { it.copy(hasPin = true, pinLockedUntil = 0L, resetStep = PinResetStep.IDLE) }
        resetToken = null
        return true
    }

    /**
     * Can this account use Kids Mode at all? Only an account the owner switched KIDZONE on for
     * (the entitlement's `kidsMode`), and only subscribers: a kid can never see the paywall. A DEV
     * server that opens KIDZONE to everyone (`kidsModeOpen`) lifts the subscriber requirement.
     */
    fun isEligible(): Boolean {
        val ent = entitlement()
        // canUseKidsMode = kidsMode && (isActiveSubscriber || kidsModeOpen)
        return SupabaseAuthService.instance.isLoggedIn && ent?.canUseKidsMode == true
    }

    /** Parent side only (the setup screen): who KIDZONE is for. Kept on the device, outside KIDZONE's reach. */
    fun setAgeBand(band: KidsAgeBand) {
        store.putString(KEY_AGE_BAND, band.wire)
        _state.update { it.copy(ageBand = band) }
    }

    /** Turn Kids Mode on. Requires a PIN and an age band; the lock is written to disk before the UI switches. */
    fun enter(): Boolean {
        if (!_state.value.hasPin || _state.value.ageBand == null) return false
        store.putString(KEY_ENABLED, "1")
        // The kid's "My checks" survive leaving and re-entering, but only for the account that made
        // them: a different parent signing in on this device starts with an empty list.
        val kept = if (store.getString(KEY_SCANS_OWNER) == currentEmail()) loadScans() else emptyList()
        _state.update { KidsUiState(enabled = true, hasPin = true, scans = kept, ageBand = it.ageBand) }
        saveScans(kept)
        return true
    }

    /** The PIN pad on the way out. On [PinExitResult.Ok] Kids Mode is OFF and the kid's titles are queued for Your Analyses. */
    fun tryExit(pin: String): PinExitResult {
        val now = currentTimeMillis()
        val lockedUntil = store.getString(KEY_LOCKED_UNTIL)?.toLongOrNull() ?: 0L
        if (lockedUntil > now) return PinExitResult.Locked(lockedUntil)
        if (!checkPin(pin)) {
            val fails = (store.getString(KEY_FAILS)?.toIntOrNull() ?: 0) + 1
            store.putString(KEY_FAILS, fails.toString())
            if (fails >= MAX_TRIES) {
                // 30s, then doubling per further miss, capped at 15 minutes.
                val waitMs = (LOCKOUT_BASE_MS shl (fails - MAX_TRIES).coerceAtMost(5)).coerceAtMost(LOCKOUT_MAX_MS)
                val until = now + waitMs
                store.putString(KEY_LOCKED_UNTIL, until.toString())
                _state.update { it.copy(pinLockedUntil = until) }
                return PinExitResult.Locked(until)
            }
            return PinExitResult.Wrong(MAX_TRIES - fails)
        }
        exitNow()
        return PinExitResult.Ok
    }

    private fun exitNow() {
        store.putString(KEY_FAILS, null)
        store.putString(KEY_LOCKED_UNTIL, null)
        // Everything the kid checked goes to the parent's Your Analyses (see MediaCheckerViewModel.importKidsScans).
        val pending = loadPendingImport() + _state.value.scans.filter { it.status == KidsScanStatus.DONE && !it.exported }
        store.putString(KEY_IMPORT, json.encodeToString(pending.distinctBy { importKey(it) }))
        // Tagged with the account, so a different account signing in on this device never inherits them.
        store.putString(KEY_IMPORT_OWNER, currentEmail())
        jobs.values.forEach { it.cancel() }
        jobs.clear()
        searchJob?.cancel(); photoJob?.cancel()
        // Keep what the kid finished for next time (a half-done check can't resume once its job is
        // cancelled, so those are dropped); the copy queued for the parent above is separate.
        saveScans(_state.value.scans.filter { it.status == KidsScanStatus.DONE }.map { it.copy(exported = true) })
        store.putString(KEY_SCANS_OWNER, currentEmail())
        store.putString(KEY_ENABLED, null)
        _state.update { KidsUiState(enabled = false, hasPin = true, ageBand = it.ageBand) }
        resetToken = null
    }

    // ── Forgot PIN: emailed code ────────────────────────────────────────────────

    fun requestPinReset() {
        _state.update { it.copy(resetStep = PinResetStep.SENDING) }
        scope.launch {
            resetToken = apiClient.requestKidsPinReset()
            _state.update { it.copy(resetStep = if (resetToken != null) PinResetStep.CODE_SENT else PinResetStep.FAILED) }
        }
    }

    fun verifyPinReset(code: String) {
        val token = resetToken ?: return
        _state.update { it.copy(resetStep = PinResetStep.VERIFYING) }
        scope.launch {
            val ok = apiClient.verifyKidsPinReset(token, code.trim())
            _state.update { it.copy(resetStep = if (ok) PinResetStep.VERIFIED else PinResetStep.CODE_SENT) }
            if (!ok) _state.update { it.copy(message = KidsMessage.OOPS) }
        }
    }

    /** After a verified code: the parent sets a new PIN, which also leaves Kids Mode. */
    fun setPinAfterReset(pin: String): Boolean {
        if (_state.value.resetStep != PinResetStep.VERIFIED) return false
        if (!setPin(pin)) return false
        if (_state.value.enabled) exitNow()
        return true
    }

    fun cancelPinReset() {
        resetToken = null
        _state.update { it.copy(resetStep = PinResetStep.IDLE) }
    }

    // ── Kid side: search, photo, pick ───────────────────────────────────────────

    fun onQueryChange(query: String) {
        val q = query.take(120)
        _state.update { it.copy(query = q, message = null, suggestions = suggestionsFor(q)) }
    }

    /**
     * Titles to suggest as the kid types, from the same index as the main search bar. That index is every title
     * anyone has checked, so the screens show these as words only (no covers), and a name with a rude or adult
     * word in it is never suggested.
     */
    private fun suggestionsFor(query: String): List<DisambiguationItem> =
        TitleAutocomplete.rank(query, titles(), limit = MAX_SUGGESTIONS * 3)
            .filter { item -> item.title.lowercase().split(Regex("[^a-z0-9]+")).none { it in UNSUGGESTED_WORDS } }
            .take(MAX_SUGGESTIONS)

    /** The kid tapped a suggested title: it's already a known title, so check it straight away (no search, no "Which one is it?"). */
    fun pickSuggestion(item: DisambiguationItem) {
        _state.update { it.copy(query = item.title, suggestions = emptyList(), message = null) }
        if (!preflight()) return
        startScan(item, journeyId = null, source = "autocomplete")
    }

    fun search(source: String = "search") {
        val query = _state.value.query.trim()
        if (query.isEmpty()) return
        if (!preflight()) return
        _state.update { it.copy(isSearching = true, message = null, suggestions = emptyList(), choices = emptyList(), photoChoices = emptyList()) }
        searchJob?.cancel()
        searchJob = scope.launch {
            repository.disambiguate(query).collect { progress ->
                when (progress) {
                    is AnalysisProgress.DisambiguationComplete -> {
                        val items = progress.items.take(MAX_CHOICES).map { it.withCachedCover() }
                        _state.update { it.copy(isSearching = false) }
                        // Search results carry no pictures. Look them all up NOW, in parallel, while
                        // the kid is still choosing, so the picked title's cover is already there.
                        items.forEach { prefetchCover(it) }
                        when (items.size) {
                            0 -> _state.update { it.copy(message = KidsMessage.NOT_FOUND) }
                            // One clear match: go straight to checking it.
                            1 -> startScan(items.first(), progress.journeyId, source)
                            else -> _state.update { it.copy(choices = items) }
                        }
                        pendingJourneyId = progress.journeyId
                        pendingSource = source
                    }
                    is AnalysisProgress.Error -> _state.update {
                        it.copy(isSearching = false, message = if (isAuthOrCredit(progress.message)) null else KidsMessage.OOPS,
                            block = if (isAuthOrCredit(progress.message)) KidsBlock.GROWN_UP_NEEDED else it.block)
                    }
                    else -> {}
                }
            }
        }
    }

    fun onPhoto(imageBytes: ByteArray) {
        if (!preflight()) return
        photoJob?.cancel()
        _state.update { it.copy(isReadingPhoto = true, message = null, choices = emptyList(), photoChoices = emptyList()) }
        photoJob = scope.launch {
            val candidates = textRecognizer.extract(imageBytes)
            _state.update { it.copy(isReadingPhoto = false) }
            when {
                candidates.isEmpty() -> _state.update { it.copy(message = KidsMessage.PHOTO_UNREADABLE) }
                candidates.size == 1 -> {
                    _state.update { it.copy(query = candidates.first().title) }
                    search("photo")
                }
                else -> _state.update { it.copy(photoChoices = candidates.take(MAX_CHOICES)) }
            }
        }
    }

    fun pickPhotoChoice(candidate: MediaCandidate) {
        _state.update { it.copy(photoChoices = emptyList(), query = candidate.title) }
        search("photo")
    }

    fun pick(item: DisambiguationItem) {
        _state.update { it.copy(choices = emptyList()) }
        startScan(item, pendingJourneyId, pendingSource)
    }

    fun clearChoices() {
        searchJob?.cancel(); photoJob?.cancel()
        _state.update { it.copy(choices = emptyList(), photoChoices = emptyList(), isSearching = false, isReadingPhoto = false) }
    }

    fun openScan(id: String) = _state.update { it.copy(openScanId = id) }

    /**
     * 8 and over, on a NO: look up "Try one of these instead" for this scan, once. The UI calls it when the
     * result shows. Free (catalog only), so no preflight; a switched-off account just gets an empty list.
     */
    fun loadAlternatives(scanId: String) {
        if (_state.value.band != KidsAgeBand.OLDER || scanId in _state.value.alternatives) return
        val scan = _state.value.scans.firstOrNull { it.id == scanId } ?: return
        _state.update { it.copy(alternatives = it.alternatives + (scanId to emptyList())) }
        scope.launch {
            val items = apiClient.fetchKidsAlternatives(scan.title, scan.mediaType.name, scan.year)
                .filterNot { it.title.equals(scan.title, ignoreCase = true) }
            _state.update { it.copy(alternatives = it.alternatives + (scanId to items)) }
        }
    }

    /** "Read it to me" clips by text, newest last: asked for once and shared by the result view and the tap. */
    private val speech = LinkedHashMap<String, Deferred<ByteArray?>>()

    /**
     * 7 and under, "Read it to me": [text] in the KIDZONE voice as a WAV file, or null for the device voice.
     * The result view asks as it opens, so the tap usually plays at once. Made in the controller's scope, so
     * leaving the screen doesn't throw a clip away half-made; a failed one is asked for again next time.
     */
    suspend fun speechAudio(text: String): ByteArray? {
        val clip = speech.getOrPut(text) { scope.async { apiClient.fetchKidsSpeech(text) } }
        while (speech.size > SPEECH_CLIPS_KEPT) speech.remove(speech.keys.first())
        val audio = clip.await()
        if (audio == null && speech[text] === clip) speech.remove(text)
        return audio
    }

    /** The kid tapped a "Try one of these instead" title: check it like any other (a cache hit for a catalog title). */
    fun checkAlternative(alt: KidsAlternative) {
        if (!preflight()) return
        startScan(DisambiguationItem(title = alt.title, year = alt.year, mediaType = alt.mediaType, coverUrl = alt.coverUrl),
            journeyId = null, source = "alternative")
    }
    fun closeScan() = _state.update { it.copy(openScanId = null, query = "", suggestions = emptyList()) }
    fun dismissMessage() = _state.update { it.copy(message = null) }
    fun dismissBlock() = _state.update { it.copy(block = null) }

    /** Retry a scan that failed. */
    fun retry(id: String) {
        val scan = _state.value.scans.firstOrNull { it.id == id } ?: return
        if (!preflight()) return
        runScan(scan.copy(status = KidsScanStatus.CHECKING, block = null, result = null), journeyId = null, source = "retry")
    }

    /**
     * App came back to the foreground: reattach any check whose stream the OS dropped, and ask the
     * cache once straight away. The answer is often already there (the server keeps going while the
     * phone sleeps), and a stream that died without closing would otherwise leave "Checking…" up.
     */
    fun onForeground() {
        if (!_state.value.enabled) return
        reattachChecking()
        _state.value.scans.filter { it.status == KidsScanStatus.CHECKING }.forEach { scan ->
            scope.launch {
                val read = repository.fetchKidsResult(scan.title, scan.mediaType, criteria(), scan.year, scan.info, scan.author)
                if (read is KidsCacheRead.Ready) {
                    handle(scan.id, KidsProgress.Done(read.result))
                    jobs.remove(scan.id)?.cancel()
                } else if (read is KidsCacheRead.NotAllowed) {
                    handle(scan.id, KidsProgress.Blocked(KidsBlock.GROWN_UP_NEEDED, notAllowed = true))
                    jobs.remove(scan.id)?.cancel()
                }
            }
        }
    }

    // ── Scans ───────────────────────────────────────────────────────────────────

    /**
     * KIDZONE not switched on for this account / not subscribed / signed out / at the daily cap →
     * "Ask a grown-up" instead of a paid call. The server refuses a switched-off account anyway; this
     * saves the paid search, photo and cover calls that come BEFORE it gets the chance to.
     */
    private fun preflight(): Boolean {
        if (entitlement()?.kidsMode != true) {
            // Switched off since this device last looked (or never on): block, and look again so the
            // device finds out quickly either way.
            _state.update { it.copy(block = KidsBlock.GROWN_UP_NEEDED) }
            refreshEntitlement()
            return false
        }
        if (!isEligible()) {
            _state.update { it.copy(block = KidsBlock.GROWN_UP_NEEDED) }
            return false
        }
        if (entitlement()?.dailyLimitNotice() != null) {
            _state.update { it.copy(block = KidsBlock.DAILY_LIMIT) }
            return false
        }
        return true
    }

    private fun startScan(item: DisambiguationItem, journeyId: String?, source: String) {
        val mediaType = parseMediaType(item.mediaType)
        // Already checked (or being checked) → just open it. No second paid analysis.
        _state.value.scans.firstOrNull {
            it.title.equals(item.title, ignoreCase = true) && it.mediaType == mediaType && it.year == item.year &&
                it.status != KidsScanStatus.FAILED && it.status != KidsScanStatus.BLOCKED
        }?.let { existing ->
            _state.update { it.copy(openScanId = existing.id) }
            return
        }
        val now = currentTimeMillis()
        val scan = KidsScan(
            id = "kid-$now-${kotlin.random.Random.nextInt(100_000)}",
            title = item.title,
            mediaType = mediaType,
            year = item.year,
            info = item.info,
            author = item.author,
            coverUrl = item.coverUrl ?: covers[coverKey(item)],
            startedAt = now
        )
        runScan(scan, journeyId, source)
        _state.update { it.copy(openScanId = scan.id) }
        // Still no picture (a single match skips the choices screen): fetch it alongside the check.
        if (scan.coverUrl == null) scope.launch {
            val url = fetchCover(item) ?: return@launch
            val current = _state.value.scans.firstOrNull { it.id == scan.id } ?: return@launch
            if (current.coverUrl == null) upsert(current.copy(coverUrl = url))
        }
    }

    private fun coverKey(item: DisambiguationItem) = "${item.title.lowercase()}::${item.mediaType}::${item.year ?: ""}"

    private fun DisambiguationItem.withCachedCover() = if (coverUrl != null) this else copy(coverUrl = covers[coverKey(this)])

    private suspend fun fetchCover(item: DisambiguationItem): String? {
        covers[coverKey(item)]?.let { return it }
        val url = try {
            apiClient.fetchCoverUrl(item.title, item.mediaType ?: parseMediaType(item.mediaType).name, item.year)
        } catch (_: Exception) { null } ?: return null
        covers[coverKey(item)] = url
        return url
    }

    /** Fill in one choice's cover as soon as it's known (the choices list may be gone by then). */
    private fun prefetchCover(item: DisambiguationItem) {
        if (item.coverUrl != null) return
        scope.launch {
            val url = fetchCover(item) ?: return@launch
            val key = coverKey(item)
            _state.update { s ->
                s.copy(choices = s.choices.map { if (it.coverUrl == null && coverKey(it) == key) it.copy(coverUrl = url) else it })
            }
            // The kid may have tapped it already.
            _state.value.scans.firstOrNull { it.coverUrl == null && it.title.equals(item.title, true) && it.year == item.year }
                ?.let { upsert(it.copy(coverUrl = url)) }
        }
    }

    private fun runScan(scan: KidsScan, journeyId: String?, source: String) {
        val cancelToken = "ct-${currentTimeMillis()}-${kotlin.random.Random.nextLong()}"
        val running = scan.copy(status = KidsScanStatus.CHECKING, journeyId = cancelToken)
        upsert(running)
        jobs[scan.id]?.cancel()
        jobs[scan.id] = scope.launch {
            repository.analyzeKids(
                title = scan.title, mediaType = scan.mediaType, criteria = criteria(),
                year = scan.year, info = scan.info, author = scan.author,
                journeyId = journeyId, searchSource = source, cancelToken = cancelToken
            ).collect { handle(scan.id, it) }
        }
    }

    /** Re-listen to every scan still CHECKING (after a restart or an OS-dropped stream). */
    private fun reattachChecking() {
        _state.value.scans.filter { it.status == KidsScanStatus.CHECKING && jobs[it.id]?.isActive != true }.forEach { scan ->
            // The cancel token doubles as the resume handle (the server registers both ids).
            val resumeId = scan.journeyId
            jobs[scan.id] = scope.launch {
                if (resumeId != null) repository.resumeKids(resumeId).collect { handle(scan.id, it) }
                else pollCache(scan.id)
            }
        }
    }

    private suspend fun handle(scanId: String, progress: KidsProgress) {
        // A scan that already has its answer (e.g. the foreground cache check won the race) never
        // goes back: a late "stream failed" must not turn a DONE into an "Oops".
        if (_state.value.scans.firstOrNull { it.id == scanId }?.status != KidsScanStatus.CHECKING) return
        when (progress) {
            is KidsProgress.Working -> progress.fraction?.let { f ->
                _state.update { s -> s.copy(progress = s.progress + (scanId to maxOf(f, s.progress[scanId] ?: 0f))) }
            }
            is KidsProgress.Done -> finish(scanId) { it.copy(status = KidsScanStatus.DONE, result = progress.result,
                coverUrl = progress.result.coverUrl ?: it.coverUrl) }
            is KidsProgress.Blocked -> {
                finish(scanId) { it.copy(status = KidsScanStatus.BLOCKED, block = progress.block) }
                _state.update { it.copy(block = progress.block) }
                // The server says KIDZONE is off for this account: learn it now, so the next search,
                // photo and cover lookup stop at preflight instead of costing a call each.
                if (progress.notAllowed) refreshEntitlement()
            }
            is KidsProgress.Failed ->
                if (progress.recoverable) pollCache(scanId)
                else finish(scanId) { it.copy(status = KidsScanStatus.FAILED) }
        }
    }

    /** The stream is gone but the server keeps going: ask the cache until the answer appears. */
    private suspend fun pollCache(scanId: String) {
        repeat(CACHE_POLLS) { attempt ->
            val scan = _state.value.scans.firstOrNull { it.id == scanId } ?: return
            if (scan.status != KidsScanStatus.CHECKING) return
            if (attempt > 0) delay(CACHE_POLL_MS)
            when (val read = repository.fetchKidsResult(scan.title, scan.mediaType, criteria(), scan.year, scan.info, scan.author)) {
                is KidsCacheRead.Ready -> {
                    handle(scanId, KidsProgress.Done(read.result))
                    return
                }
                // This server has no Kids Mode: it will never answer, so don't make the kid wait.
                KidsCacheRead.Unsupported -> {
                    finish(scanId) { it.copy(status = KidsScanStatus.FAILED) }
                    return
                }
                // KIDZONE was switched off for this account: "Ask a grown-up", not a wait.
                KidsCacheRead.NotAllowed -> {
                    handle(scanId, KidsProgress.Blocked(KidsBlock.GROWN_UP_NEEDED, notAllowed = true))
                    return
                }
                KidsCacheRead.NotReady -> {}
            }
        }
        finish(scanId) { it.copy(status = KidsScanStatus.FAILED) }
    }

    private fun finish(scanId: String, transform: (KidsScan) -> KidsScan) {
        val scan = _state.value.scans.firstOrNull { it.id == scanId } ?: return
        upsert(transform(scan))
        _state.update { it.copy(progress = it.progress - scanId) }
    }

    private fun upsert(scan: KidsScan) {
        _state.update { s ->
            val others = s.scans.filter { it.id != scan.id }
            val existingIndex = s.scans.indexOfFirst { it.id == scan.id }
            val updated = if (existingIndex >= 0) s.scans.toMutableList().also { it[existingIndex] = scan } else listOf(scan) + others
            s.copy(scans = updated.take(MAX_SCANS))
        }
        saveScans(_state.value.scans)
    }

    // ── Import into the parent's Your Analyses (called by the ViewModel, parent side only) ──

    /** Titles the kid checked, waiting to be added to the signed-in parent's history. */
    fun pendingImport(): List<KidsScan> {
        val owner = store.getString(KEY_IMPORT_OWNER)
        if (owner != null && owner != currentEmail()) return emptyList()
        return loadPendingImport()
    }

    /** Mark these as imported. */
    fun clearImported(imported: List<KidsScan>) {
        val done = imported.map { importKey(it) }.toSet()
        val left = loadPendingImport().filter { importKey(it) !in done }
        store.putString(KEY_IMPORT, if (left.isEmpty()) null else json.encodeToString(left))
        if (left.isEmpty()) store.putString(KEY_IMPORT_OWNER, null)
    }

    private fun currentEmail(): String? = try { SupabaseAuthService.instance.currentUser?.email } catch (_: Exception) { null }

    // ── Storage ─────────────────────────────────────────────────────────────────

    private fun checkPin(pin: String): Boolean {
        val stored = store.getString(KEY_PIN) ?: return false
        val salt = stored.substringBefore(":")
        return Sha256.hex("$salt:$pin") == stored.substringAfter(":")
    }

    private fun loadScans(): List<KidsScan> = try {
        store.getString(KEY_SCANS)?.let { json.decodeFromString<List<KidsScan>>(it) } ?: emptyList()
    } catch (_: Exception) { emptyList() }

    private fun saveScans(scans: List<KidsScan>) =
        store.putString(KEY_SCANS, if (scans.isEmpty()) null else json.encodeToString(scans))

    private fun loadPendingImport(): List<KidsScan> = try {
        store.getString(KEY_IMPORT)?.let { json.decodeFromString<List<KidsScan>>(it) } ?: emptyList()
    } catch (_: Exception) { emptyList() }

    private fun importKey(scan: KidsScan) = "${scan.title.lowercase()}::${scan.mediaType}::${scan.year ?: ""}"

    private fun isAuthOrCredit(message: String) =
        message.contains("auth_required") || message.contains("no_credits") || message.contains("402") ||
            message.contains("session has expired")

    companion object {
        const val MAX_CHOICES = 4
        const val MAX_SUGGESTIONS = 5
        /** A clip is a few hundred KB; a kid flicks between a handful of answers at most. */
        private const val SPEECH_CLIPS_KEPT = 6
        /** Whole words that keep a title out of the suggestions (it can still be typed and checked). */
        private val UNSUGGESTED_WORDS = setOf(
            "sex", "sexy", "sexual", "porn", "porno", "nude", "nudes", "naked", "erotic", "erotica", "xxx", "fuck", "fucking",
            "shit", "bitch", "bitches", "bastard", "dick", "cock", "pussy", "whore", "slut", "damn", "hell", "ass", "milf",
        )
        const val MAX_TRIES = 5
        private const val MAX_SCANS = 60
        private const val LOCKOUT_BASE_MS = 30_000L
        private const val LOCKOUT_MAX_MS = 15 * 60_000L
        private const val CACHE_POLLS = 30          // × 10s ≈ 5 minutes
        private const val CACHE_POLL_MS = 10_000L

        private const val KEY_ENABLED = "enabled"
        private const val KEY_PIN = "pin"
        private const val KEY_FAILS = "fails"
        private const val KEY_LOCKED_UNTIL = "locked_until"
        private const val KEY_SCANS = "scans"
        private const val KEY_SCANS_OWNER = "scans_owner"
        private const val KEY_IMPORT = "import_pending"
        private const val KEY_IMPORT_OWNER = "import_owner"
        private const val KEY_AGE_BAND = "age_band"

        fun isValidPin(pin: String) = pin.length == 4 && pin.all { it in '0'..'9' }

        fun parseMediaType(typeString: String?): MediaType = when (typeString?.lowercase()) {
            "tv_show", "tv_shows", "tv show", "tv series" -> MediaType.TV_SHOWS
            "game", "games", "video game", "video_game" -> MediaType.GAMES
            "youtube", "youtube channel" -> MediaType.YOUTUBE
            "book", "books" -> MediaType.BOOKS
            else -> MediaType.MOVIES
        }
    }
}
